/**
 * A firm's Case Notes on its Firm Success page (#692 wave 2).
 *
 * WHAT THIS ANSWERS THAT THE REST OF THE PAGE CANNOT. Every other block on Firm
 * Success is derived — counts, conversion, clocks, a map. Those say what
 * happened *to* this firm's matters. This says what LF observed *about* the
 * firm: it called three times and got nothing back, it asked for records it had
 * already been sent, it turned a case down for a reason worth remembering. That
 * knowledge exists today only in whoever did the calling.
 *
 * Notes are shown for the capacity being viewed — a firm's handling record and
 * its originating record answer different questions and are not pooled.
 *
 * Read-only here. Notes are written where the work happens, on the case, and a
 * note has to anchor to a specific matter; a firm-level compose box would have
 * nothing to attach to.
 */

import type { CaseNote } from "@command-center/shared";
import { useFirmCaseNotes } from "@/lib/use-case-notes";

function fmtDay(d: string): string {
  const [y, m, day] = d.split("-");
  return y && m && day ? `${Number(m)}/${Number(day)}/${y}` : d;
}

function NoteList({ notes, testId }: { notes: CaseNote[]; testId: string }) {
  return (
    <ol className="flex flex-col gap-3 text-sm" data-testid={testId}>
      {notes.map((n) => (
        <li key={n.id} className="flex flex-col gap-0.5">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-xs tabular-nums text-muted-foreground">
              {fmtDay(n.occurredOn)}
            </span>
            <span className="text-xs text-muted-foreground">
              {n.author.split("@")[0] ?? n.author}
            </span>
          </div>
          <span className="whitespace-pre-wrap">{n.body}</span>
        </li>
      ))}
    </ol>
  );
}

export function FirmCaseNotes({
  firmId,
  capacity,
  testId = "fs-notes",
}: {
  firmId: string;
  /** Which record is being read — a firm's two capacities are not pooled. */
  capacity: "of" | "hf";
  testId?: string;
}) {
  const q = useFirmCaseNotes(firmId, true);

  if (q.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-loading`}>
        Loading notes…
      </p>
    );
  }
  if (q.isError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-error`}>
        Couldn't load notes. Reload the page to retry.
      </p>
    );
  }

  const notes = (capacity === "hf" ? q.data?.hf : q.data?.of) ?? [];
  if (notes.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground" data-testid={`${testId}-empty`}>
        {/* Says what is true — nobody has written one — rather than implying
            nothing has happened. Absence of a note is absence of writing. */}
        No internal notes recorded on this firm yet.
      </p>
    );
  }

  return <NoteList notes={notes} testId={`${testId}-list`} />;
}
